/**
 * Schichtplanung — Wochen-basierte Schichtplanung mit Drag & Drop und Auto-Vorschlag.
 *
 * Features:
 * - Wochenansicht: MA-Zuweisung zu Schichten pro Tag
 * - Auto-Vorschlag basierend auf Verfuegbarkeit und Bedarf
 * - FTE-Gauge pro Tag, Abwesenheits-Erkennung
 * - Schichtmodell- und Standort-Filter
 *
 * @page /schichtplanung
 * @roles admin, manager
 * @api /api/schichtplanung/woche, /api/schichtplanung, /api/schichtplanung/auto-vorschlag
 * @api /api/schichtplanung/bedarf, /api/schichtmodelle
 * @api /api/mitarbeiter, /api/verfuegbarkeit
 * @i18n shifts
 */
import { useState, useEffect, useCallback, useMemo } from 'react';
import { NavLink } from 'react-router-dom';
import { useStandort } from '../hooks/useStandort';
import StandortSelect from '../components/StandortSelect';
import { PageHeader, Badge, Button } from '../components/ui';
import { useLanguage } from '../context/LanguageContext';

// ─── Helpers ─────────────────────────────────────────────────────

function api(path, opts = {}) {
  const headers = opts.body ? { 'Content-Type': 'application/json' } : {};
  return fetch(path, {
    ...opts,
    headers: { ...headers, ...opts.headers },
    body: opts.body ? JSON.stringify(opts.body) : undefined,
  }).then((r) => r.json());
}

const weekdayKeys = ['montag', 'dienstag', 'mittwoch', 'donnerstag', 'freitag', 'samstag', 'sonntag'];
const rolleColors = { picker: '#338edc', packer: '#66ABE5', both: '#06b6d4' };
const abwColors = { urlaub: '#338edc', krank: '#ef4444', za: '#f59e0b', sonstig: '#4D9CE0' };

function dateStr(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function getMonday(d) {
  const date = new Date(d);
  const day = date.getDay();
  const diff = date.getDate() - day + (day === 0 ? -6 : 1);
  date.setDate(diff);
  date.setHours(0, 0, 0, 0);
  return date;
}

function getKW(date) {
  const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
  d.setUTCDate(d.getUTCDate() + 4 - (d.getUTCDay() || 7));
  const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
  return Math.ceil(((d - yearStart) / 86400000 + 1) / 7);
}

function fmtTime(t) { return t ? t.slice(0, 5) : ''; }

function normDate(d) {
  if (!d) return '';
  if (typeof d === 'string') {
    // ISO datetime strings (e.g. "2026-02-24T23:00:00.000Z") need local timezone parsing
    if (d.includes('T')) return dateStr(new Date(d));
    return d.slice(0, 10);
  }
  return dateStr(d);
}

// ─── FTE Gauge ───────────────────────────────────────────────────

function FteGauge({ label, planned, needed, color }) {
  const max = Math.max(planned, needed, 0.1);
  const pctPlanned = Math.min((planned / max) * 100, 100);
  const pctNeeded = Math.min((needed / max) * 100, 100);
  const ok = planned >= needed * 0.95;
  return (
    <div className="mb-1.5">
      <div className="flex justify-between text-[10px] mb-0.5">
        <span className="text-[var(--text-secondary)]">{label}</span>
        <span className="font-mono font-medium" style={{ color: ok ? '#338edc' : '#22D3EE' }}>
          {planned.toFixed(1)}<span className="text-content-tertiary">/{needed.toFixed(1)}</span>
        </span>
      </div>
      <div className="h-1.5 bg-surface-inset rounded-full overflow-hidden relative">
        <div className="absolute h-full rounded-full opacity-30" style={{ width: `${pctNeeded}%`, backgroundColor: color }} />
        <div className="absolute h-full rounded-full" style={{ width: `${pctPlanned}%`, backgroundColor: ok ? '#338edc' : '#22D3EE' }} />
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
// SCHICHTPLANUNG
// ═══════════════════════════════════════════════════════════════════

export default function Schichtplanung() {
  const { t, tMonth, locale } = useLanguage();
  const [weekStart, setWeekStart] = useState(() => getMonday(new Date()));
  const [schichtmodelle, setSchichtmodelle] = useState([]);
  const [mitarbeiter, setMitarbeiter] = useState([]);
  const [zuweisungen, setZuweisungen] = useState([]);
  const [bedarf, setBedarf] = useState([]);
  const [abwesenheiten, setAbwesenheiten] = useState([]);
  const [search, setSearch] = useState('');
  const [rolleFilter, setRolleFilter] = useState('alle');
  const [loading, setLoading] = useState(false);
  const [dropTarget, setDropTarget] = useState(null); // "day-schicht-rolle"
  const [flashTarget, setFlashTarget] = useState(null); // green flash after drop
  const [toast, setToast] = useState(null); // { msg, type: 'error'|'success' }
  const { standorte, standortId, setStandortId } = useStandort();

  const today = new Date().toLocaleDateString('en-CA', { timeZone: 'Europe/Vienna' });
  const kw = getKW(weekStart);

  // ── Week days ──
  const weekDays = useMemo(() =>
    Array.from({ length: 7 }, (_, i) => {
      const d = new Date(weekStart);
      d.setDate(weekStart.getDate() + i);
      return {
        date: d, str: dateStr(d),
        name: t(`common.weekdays_long.${weekdayKeys[i]}`),
        short: t(`common.weekdays_short.${weekdayKeys[i]}`),
        num: d.getDate(), month: d.getMonth() + 1,
        isWeekend: i >= 5, isToday: dateStr(d) === today, isPast: dateStr(d) < today,
      };
    }), [weekStart, today, t]);

  const weekEndStr = weekDays[6].str;
  const rangeStr = `${weekDays[0].num}.–${weekDays[6].num}. ${tMonth(weekDays[0].date.getMonth())} ${weekDays[0].date.getFullYear()}`;

  // ── Load data ──
  const loadData = useCallback(async () => {
    if (!standortId) return;
    const mon = weekDays[0].str;
    const sun = weekEndStr;
    const sp = `standort_id=${standortId}`;
    const [sm, ma, zuw, bed, abw] = await Promise.all([
      api(`/api/schichtmodelle?${sp}`),
      api(`/api/mitarbeiter?department=Lager&${sp}`),
      api(`/api/schichtplanung/woche?datum=${mon}&${sp}`),
      api(`/api/schichtplanung/bedarf?datum_von=${mon}&datum_bis=${sun}&${sp}`),
      api(`/api/verfuegbarkeit?datum_von=${mon}&datum_bis=${sun}`),
    ]);
    setSchichtmodelle(Array.isArray(sm) ? sm : []);
    setMitarbeiter(Array.isArray(ma) ? ma : []);
    setZuweisungen(Array.isArray(zuw) ? zuw : []);
    setBedarf(Array.isArray(bed) ? bed : []);
    setAbwesenheiten(Array.isArray(abw) ? abw : []);
  }, [weekDays, weekEndStr, standortId]);

  useEffect(() => { loadData(); }, [loadData]);

  // ── Navigation ──
  const prevWeek = () => { const d = new Date(weekStart); d.setDate(d.getDate() - 7); setWeekStart(d); };
  const nextWeek = () => { const d = new Date(weekStart); d.setDate(d.getDate() + 7); setWeekStart(d); };
  const goToday = () => setWeekStart(getMonday(new Date()));

  // ── Data lookups ──
  const getZuw = useCallback((dayStr, schichtId, rolle) =>
    zuweisungen.filter((z) => normDate(z.datum) === dayStr && z.schicht_id === schichtId && z.rolle === rolle),
    [zuweisungen]);

  const getBedarf = useCallback((dayStr) => {
    const b = bedarf.find((b) => normDate(b.datum) === dayStr);
    return b || { soll_pakete: 0, pick_fte_bedarf: 0, pack_fte_bedarf: 0 };
  }, [bedarf]);

  const getAbw = useCallback((dayStr) =>
    abwesenheiten.filter((a) => dayStr >= normDate(a.datum_von) && dayStr <= normDate(a.datum_bis)),
    [abwesenheiten]);

  const getPlannedFte = useCallback((dayStr, rolle) =>
    zuweisungen.filter((z) => normDate(z.datum) === dayStr && z.rolle === rolle).length,
    [zuweisungen]);

  // ── Drag & Drop ──
  const handleDragStart = (e, ma) => {
    e.dataTransfer.setData('text/plain', JSON.stringify({ id: ma.id, name: ma.name, kuerzel: ma.kuerzel, rolle: ma.rolle }));
    e.dataTransfer.effectAllowed = 'copy';
  };

  const handleDrop = async (e, dayStr, schichtId, rolle) => {
    e.preventDefault();
    setDropTarget(null);
    // Block drops on past days
    if (dayStr < today) {
      setToast({ msg: t('shifts.past_days_locked'), type: 'error' });
      return;
    }
    const zoneKey = `${dayStr}-${schichtId}-${rolle}`;
    try {
      const maData = JSON.parse(e.dataTransfer.getData('text/plain'));

      // Block drop if MA has absence on that day
      if (hasAbsence(maData.id, dayStr)) {
        setToast({ msg: t('shifts.absence_on_day', { name: maData.kuerzel }), type: 'error' });
        return;
      }

      // If already assigned on this day: remove old assignment first (move)
      const existing = zuweisungen.find((z) => z.mitarbeiter_id === maData.id && normDate(z.datum) === dayStr);
      if (existing) {
        await api(`/api/schichtplanung/${existing.id}`, { method: 'DELETE' });
      }

      await api('/api/schichtplanung', {
        method: 'POST',
        body: { datum: dayStr, mitarbeiter_id: maData.id, schicht_id: schichtId, standort_id: standortId, rolle },
      });

      // Green flash
      setFlashTarget(zoneKey);
      setTimeout(() => setFlashTarget(null), 600);

      loadData();
    } catch (err) { console.error(err); }
  };

  const handleDragOver = (e, key, dayStr) => {
    // Don't activate drop zone for past days
    if (dayStr < today) {
      e.dataTransfer.dropEffect = 'none';
      return;
    }
    e.preventDefault();
    e.dataTransfer.dropEffect = 'copy';
    setDropTarget(key);
  };

  const handleDragLeave = () => setDropTarget(null);

  const handleRemove = async (id) => {
    try {
      await api(`/api/schichtplanung/${id}`, { method: 'DELETE' });
      await loadData();
    } catch (err) {
      console.error('[handleRemove] error:', err);
    }
  };

  const handleAutoVorschlag = async () => {
    setLoading(true);
    await api('/api/schichtplanung/auto-vorschlag', {
      method: 'POST',
      body: { standort_id: standortId, datum_von: weekDays[0].str, datum_bis: weekDays[4].str },
    });
    await loadData();
    setLoading(false);
  };

  // ── Filtered MA for sidebar ──
  const filteredMa = useMemo(() => {
    let list = mitarbeiter;
    if (rolleFilter !== 'alle') list = list.filter((m) => m.rolle === rolleFilter);
    if (search) {
      const s = search.toLowerCase();
      list = list.filter((m) => m.name.toLowerCase().includes(s) || m.kuerzel.toLowerCase().includes(s));
    }
    return list;
  }, [mitarbeiter, rolleFilter, search]);

  // ── Wochen-Bilanz ──
  const bilanz = useMemo(() => {
    let pickBedarf = 0, packBedarf = 0, pickGeplant = 0, packGeplant = 0;
    weekDays.filter((d) => !d.isWeekend).forEach((d) => {
      const b = getBedarf(d.str);
      pickBedarf += parseFloat(b.pick_fte_bedarf) || 0;
      packBedarf += parseFloat(b.pack_fte_bedarf) || 0;
      pickGeplant += getPlannedFte(d.str, 'picker');
      packGeplant += getPlannedFte(d.str, 'packer');
    });
    return { pickBedarf, packBedarf, pickGeplant, packGeplant };
  }, [weekDays, getBedarf, getPlannedFte]);

  // ── Check if MA is already assigned on a day ──
  const isAssigned = useCallback((maId, dayStr) =>
    zuweisungen.some((z) => z.mitarbeiter_id === maId && normDate(z.datum) === dayStr),
    [zuweisungen]);

  // ── Check if MA has absence on a day ──
  const hasAbsence = useCallback((maId, dayStr) =>
    abwesenheiten.some((a) => a.mitarbeiter_id === maId && dayStr >= normDate(a.datum_von) && dayStr <= normDate(a.datum_bis)),
    [abwesenheiten]);

  // ── Available MA count (active, per rolle) ──
  const maCount = useMemo(() => {
    let pick = 0, pack = 0;
    mitarbeiter.forEach((m) => {
      if (m.rolle === 'picker') pick++;
      else if (m.rolle === 'packer') pack++;
      else if (m.rolle === 'both') { pick++; pack++; }
    });
    return { pick, pack, total: mitarbeiter.length };
  }, [mitarbeiter]);

  // ── Toast auto-dismiss ──
  useEffect(() => {
    if (toast) { const timer = setTimeout(() => setToast(null), 3000); return () => clearTimeout(timer); }
  }, [toast]);

  // ═══════════════════════════════════════════════════════════════
  // RENDER
  // ═══════════════════════════════════════════════════════════════

  return (
    <div className="p-page h-full flex flex-col">
      {/* Header */}
      <PageHeader
        title={t('shifts.title', { kw })}
        subtitle={rangeStr}
        className="mb-5"
        actions={
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-1">
              <Button variant="ghost" size="sm" onClick={prevWeek}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M15 18l-6-6 6-6"/></svg>
              </Button>
              <Button variant="ghost" size="sm" onClick={goToday}>{t('shifts.today')}</Button>
              <Button variant="ghost" size="sm" onClick={nextWeek}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M9 18l6-6-6-6"/></svg>
              </Button>
            </div>
            <StandortSelect standorte={standorte} standortId={standortId} setStandortId={setStandortId} />
            <Button variant="secondary" size="sm" onClick={handleAutoVorschlag} loading={loading}
              className="bg-schicht/15 text-schicht hover:bg-schicht/25 border-schicht/20">
              {!loading && <span>&#x1FA84;</span>} {t('shifts.auto_suggest')}
            </Button>
            <NavLink to={`/kunden-rotation?datum=${weekDays[0].str}`}>
              <Button variant="secondary" size="sm">{t('shifts.to_rotation')}</Button>
            </NavLink>
          </div>
        }
      />

      {/* Main content: sidebar + grid */}
      <div className="flex gap-3 flex-1 min-h-0">
        {/* ── Sidebar ── */}
        <div className="w-52 flex-shrink-0 flex flex-col gap-3">
          {/* Search */}
          <div className="relative">
            <svg className="absolute left-2.5 top-2.5 w-3.5 h-3.5 text-content-tertiary" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8"/><path d="M21 21l-4.35-4.35"/></svg>
            <input type="text" value={search} onChange={(e) => setSearch(e.target.value)} placeholder={t('shifts.search_employee')}
              className="w-full bg-surface-inset border border-line-hover rounded-lg pl-8 pr-3 py-2 text-xs text-content placeholder-content-dim focus:outline-none focus:border-schicht/40" />
          </div>

          {/* Role filter */}
          <div className="flex bg-surface-inset rounded-card p-0.5 border border-line-hover">
            {[
              { key: 'alle', labelKey: 'shifts.filter_all' },
              { key: 'picker', labelKey: 'shifts.filter_pick' },
              { key: 'packer', labelKey: 'shifts.filter_pack' },
              { key: 'both', label: '\u21C4' },
            ].map((r) => (
              <button key={r.key} onClick={() => setRolleFilter(r.key)}
                className={`flex-1 px-1.5 py-1 rounded text-[10px] transition-colors ${rolleFilter === r.key ? 'bg-surface-card text-content font-semibold shadow-sm' : 'text-[var(--text-secondary)] font-normal hover:text-content-secondary'}`}>
                {r.labelKey ? t(r.labelKey) : r.label}
              </button>
            ))}
          </div>

          {/* MA list */}
          <div className="flex-1 overflow-y-auto space-y-1 pr-1">
            {filteredMa.map((ma) => (
              <div key={ma.id} draggable
                onDragStart={(e) => handleDragStart(e, ma)}
                className="flex items-center gap-2 px-2.5 py-2 rounded-card bg-surface-card border border-line shadow-card cursor-grab hover:bg-surface-hover transition-colors active:cursor-grabbing">
                <span className="w-7 h-7 rounded-lg flex items-center justify-center text-[10px] font-bold flex-shrink-0"
                  style={{ backgroundColor: rolleColors[ma.rolle] + '20', color: rolleColors[ma.rolle] }}>
                  {ma.kuerzel}
                </span>
                <div className="flex-1 min-w-0">
                  <div className="text-xs font-medium text-content-secondary truncate">{ma.name}</div>
                  <div className="text-[10px] text-content-tertiary">
                    {t('shifts.rolle_' + ma.rolle)}
                    <span className="text-content-tertiary"> · {Number(ma.wochenstunden_soll)}h</span>
                  </div>
                </div>
              </div>
            ))}
            {filteredMa.length === 0 && (
              <div className="text-center text-xs text-content-tertiary py-4">{t('shifts.no_employees_found')}</div>
            )}
          </div>

          {/* Wochen-Bilanz */}
          <div className="bg-surface-card border border-line rounded-card p-3 flex-shrink-0 shadow-card">
            <div className="text-[10px] font-bold text-[var(--text-secondary)] uppercase tracking-wider mb-2.5">{t('shifts.weekly_balance')}</div>
            <FteGauge label={t('shifts.picking_total')} planned={bilanz.pickGeplant} needed={bilanz.pickBedarf} color="#338edc" />
            {bilanz.pickGeplant < bilanz.pickBedarf * 0.95 && (
              <div className="text-[9px] font-mono font-bold text-content mb-1 text-right">{t('shifts.gap', { value: (bilanz.pickGeplant - bilanz.pickBedarf).toFixed(1) })}</div>
            )}
            <FteGauge label={t('shifts.packing_total')} planned={bilanz.packGeplant} needed={bilanz.packBedarf} color="#66ABE5" />
            {bilanz.packGeplant < bilanz.packBedarf * 0.95 && (
              <div className="text-[9px] font-mono font-bold text-content mb-1 text-right">{t('shifts.gap', { value: (bilanz.packGeplant - bilanz.packBedarf).toFixed(1) })}</div>
            )}
            <div className="border-t border-line mt-2 pt-2 grid grid-cols-2 gap-1 text-[10px]">
              <div><span className="text-content-secondary">{t('shifts.planned')}</span><div className="font-mono font-bold text-content">{(bilanz.pickGeplant + bilanz.packGeplant).toFixed(0)} FTE</div></div>
              <div><span className="text-content-secondary">{t('shifts.demand')}</span><div className="font-mono font-bold text-schicht">{(bilanz.pickBedarf + bilanz.packBedarf).toFixed(1)} FTE</div></div>
            </div>
            {(() => {
              const totalGeplant = bilanz.pickGeplant + bilanz.packGeplant;
              const totalBedarf = bilanz.pickBedarf + bilanz.packBedarf;
              const diff = totalGeplant - totalBedarf;
              if (diff < -0.5) return (
                <div className="mt-1.5 px-2 py-1 rounded-lg bg-cyan-500/10 border border-cyan-500/20 text-[9px] font-bold text-content text-center">
                  {t('shifts.total_gap', { value: diff.toFixed(1) })}
                </div>
              );
              if (diff >= 0) return (
                <div className="mt-1.5 px-2 py-1 rounded-lg bg-primary-500/10 border border-primary-500/20 text-[9px] font-bold text-content text-center">
                  {t('shifts.capacity_ok', { value: diff.toFixed(1) })}
                </div>
              );
              return null;
            })()}
          </div>
        </div>

        {/* ── Week grid ── */}
        <div className="flex-1 overflow-x-auto">
          <div className="flex gap-2 pt-5" style={{ minWidth: 840 }}>
            {weekDays.map((day) => {
              const b = getBedarf(day.str);
              const abw = getAbw(day.str);
              const pickP = getPlannedFte(day.str, 'picker');
              const packP = getPlannedFte(day.str, 'packer');
              const pickN = parseFloat(b.pick_fte_bedarf) || 0;
              const packN = parseFloat(b.pack_fte_bedarf) || 0;
              const pakete = Math.round(parseFloat(b.soll_pakete) || 0);
              const pickOvercap = pickN > maCount.pick;
              const packOvercap = packN > maCount.pack;

              if (day.isWeekend) {
                return (
                  <div key={day.str} className="w-12 flex-shrink-0 flex flex-col gap-2 opacity-30">
                    <div className="text-center py-2 rounded-card border border-line bg-surface-card shadow-card">
                      <div className="text-[10px] font-bold uppercase tracking-wider text-content-tertiary">{day.short}</div>
                      <div className="text-lg font-bold text-content-tertiary">{day.num}</div>
                    </div>
                    <div className="flex-1 flex items-center justify-center bg-surface-card border border-line rounded-card shadow-card">
                      <span className="text-[8px] text-content-tertiary [writing-mode:vertical-lr] rotate-180">{t('shifts.weekend')}</span>
                    </div>
                  </div>
                );
              }

              return (
                <div key={day.str} className={`flex-1 min-w-[110px] flex flex-col gap-2${day.isPast ? ' opacity-40' : ''}`}>
                  {/* Today badge above header */}
                  {day.isToday && (
                    <div className="flex justify-center -mb-4 relative z-10">
                      <Badge variant="info" className="text-[8px] py-0.5 uppercase tracking-wider bg-schicht text-white">{t('shifts.today_badge')}</Badge>
                    </div>
                  )}
                  {/* Day header */}
                  <div className={`text-center py-2 rounded-card border transition-colors shadow-card relative ${
                    day.isToday
                      ? 'bg-surface-card border-line pt-3'
                      : day.isPast
                        ? 'bg-gray-100 dark:bg-gray-800/50 border-line'
                        : 'bg-surface-card border-line'
                  }`}>
                    {day.isPast && (
                      <span className="absolute top-1.5 right-1.5 text-content-tertiary opacity-60" title={t('shifts.past_title')}>
                        <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>
                      </span>
                    )}
                    <div className={`text-[10px] font-bold uppercase tracking-wider ${day.isPast ? 'text-content-tertiary' : 'text-[var(--text-secondary)]'}`}>{day.short}</div>
                    <div className={`text-xl font-bold ${day.isPast ? 'text-content-tertiary' : 'text-content'}`}>{day.num}</div>
                  </div>

                  {/* FTE demand */}
                  <div className="bg-surface-card border border-line rounded-card p-2.5 shadow-card">
                    <FteGauge label="Picking" planned={pickP} needed={pickN} color="#338edc" />
                    <FteGauge label="Packing" planned={packP} needed={packN} color="#66ABE5" />
                    {pakete > 0 && (
                      <div className="text-center mt-1">
                        <span className="text-[10px] font-mono text-[var(--text-secondary)]">{t('shifts.pkg_label', { n: pakete.toLocaleString(locale) })}</span>
                      </div>
                    )}
                    {(pickOvercap || packOvercap) && (
                      <div className="mt-1.5 px-1.5 py-1 rounded bg-orange-500/10 border border-orange-500/20 text-[8px] font-bold text-orange-400 text-center">
                        {t('shifts.demand_exceeds', { n: maCount.total })}
                      </div>
                    )}
                  </div>

                  {/* Abwesenheiten */}
                  {abw.length > 0 && (
                    <div className="space-y-0.5">
                      {abw.map((a, i) => {
                        const maObj = mitarbeiter.find((m) => m.id === a.mitarbeiter_id);
                        return (
                          <div key={i} className="flex items-center gap-1 px-2 py-1 rounded-lg text-[10px] font-medium"
                            style={{ backgroundColor: abwColors[a.typ] + '12', color: abwColors[a.typ] }}>
                            <span className="font-bold">{maObj?.kuerzel || '??'}</span>
                            <span className="truncate">{t('shifts.abs_' + a.typ)}</span>
                          </div>
                        );
                      })}
                    </div>
                  )}

                  {/* Schicht blocks */}
                  {schichtmodelle.map((sm) => (
                    <div key={sm.id} className="bg-surface-card border border-line rounded-card overflow-hidden shadow-card">
                      {/* Schicht header */}
                      <div className="px-2.5 py-1.5 bg-surface-card border-b border-line">
                        <div className="text-[10px] font-bold text-content-tertiary">{sm.name}</div>
                        <div className="text-[9px] text-content-tertiary font-mono">{fmtTime(sm.start_zeit)}–{fmtTime(sm.end_zeit)}</div>
                      </div>

                      {/* Picker drop zone */}
                      {(() => {
                        const zoneKey = `${day.str}-${sm.id}-picker`;
                        const chips = getZuw(day.str, sm.id, 'picker');
                        const isOver = !day.isPast && dropTarget === zoneKey;
                        const isFlash = flashTarget === zoneKey;
                        return (
                          <div className={`px-2 py-1.5 border-b border-line transition-all duration-300 ${day.isPast ? 'pointer-events-none' : ''} ${isFlash ? 'bg-primary-500/15' : isOver ? 'bg-primary-500/10 ring-1 ring-inset ring-primary-500/30' : ''}`}
                            onDragOver={(e) => handleDragOver(e, zoneKey, day.str)}
                            onDragLeave={handleDragLeave}
                            onDrop={(e) => handleDrop(e, day.str, sm.id, 'picker')}>
                            <div className="text-[9px] font-bold text-primary-400/50 uppercase tracking-wider mb-1">{t('shifts.picker')}</div>
                            <div className="flex flex-wrap gap-1 min-h-[22px]">
                              {chips.map((z) => (
                                <span key={z.id}
                                  className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-bold bg-primary-500/15 text-primary-400 group"
                                  title={z.ma_name}>
                                  {z.ma_kuerzel}
                                  {!day.isPast && (
                                    <button type="button" onClick={(e) => { e.stopPropagation(); handleRemove(z.id); }}
                                      className="opacity-60 group-hover:opacity-100 text-primary-400 hover:text-red-400 transition-opacity ml-0.5 leading-none cursor-pointer">&#x2715;</button>
                                  )}
                                </span>
                              ))}
                              {chips.length === 0 && !day.isPast && (
                                <span className={`text-[9px] italic ${isOver ? 'text-primary-400' : 'text-content-tertiary'}`}>
                                  {isOver ? t('shifts.drop_here') : t('shifts.add_picker')}
                                </span>
                              )}
                            </div>
                          </div>
                        );
                      })()}

                      {/* Packer drop zone */}
                      {(() => {
                        const zoneKey = `${day.str}-${sm.id}-packer`;
                        const chips = getZuw(day.str, sm.id, 'packer');
                        const isOver = !day.isPast && dropTarget === zoneKey;
                        const isFlash = flashTarget === zoneKey;
                        return (
                          <div className={`px-2 py-1.5 transition-all duration-300 ${day.isPast ? 'pointer-events-none' : ''} ${isFlash ? 'bg-primary-500/15' : isOver ? 'bg-purple-500/10 ring-1 ring-inset ring-purple-500/30' : ''}`}
                            onDragOver={(e) => handleDragOver(e, zoneKey, day.str)}
                            onDragLeave={handleDragLeave}
                            onDrop={(e) => handleDrop(e, day.str, sm.id, 'packer')}>
                            <div className="text-[9px] font-bold text-purple-400/50 uppercase tracking-wider mb-1">{t('shifts.packer')}</div>
                            <div className="flex flex-wrap gap-1 min-h-[22px]">
                              {chips.map((z) => (
                                <span key={z.id}
                                  className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-bold bg-purple-500/15 text-purple-400 group"
                                  title={z.ma_name}>
                                  {z.ma_kuerzel}
                                  {!day.isPast && (
                                    <button type="button" onClick={(e) => { e.stopPropagation(); handleRemove(z.id); }}
                                      className="opacity-60 group-hover:opacity-100 text-purple-400 hover:text-red-400 transition-opacity ml-0.5 leading-none cursor-pointer">&#x2715;</button>
                                  )}
                                </span>
                              ))}
                              {chips.length === 0 && !day.isPast && (
                                <span className={`text-[9px] italic ${isOver ? 'text-purple-400' : 'text-content-tertiary'}`}>
                                  {isOver ? t('shifts.drop_here') : t('shifts.add_packer')}
                                </span>
                              )}
                            </div>
                          </div>
                        );
                      })()}
                    </div>
                  ))}

                  {schichtmodelle.length === 0 && (
                    <div className="text-center text-[10px] text-content-tertiary py-4 bg-surface-card border border-line rounded-card shadow-card">
                      {t('shifts.no_shift_models')}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Toast notification */}
      {toast && (
        <div className={`fixed bottom-6 right-6 z-50 px-4 py-3 rounded-card shadow-2xl border text-sm font-semibold flex items-center gap-2 animate-[slideUp_0.3s_ease-out] ${
          toast.type === 'error'
            ? 'bg-red-950/90 border-red-500/30 text-red-400'
            : 'bg-green-950/90 border-green-500/30 text-green-400'
        }`}>
          {toast.type === 'error' ? '\u26A0' : '\u2713'} {toast.msg}
        </div>
      )}
    </div>
  );
}
